/**
 * Normaliza números de telefone colados em diversos formatos
 * para um formato válido de 11 dígitos (DDD + 9 dígitos)
 * Formato final para ligação: DDDNNNNNNNNN (ex: 48992033200)
 *
 * Garante que o número final seja válido para realizar ligações
 */
export function normalizePhoneNumberPaste(pastedText: string): string {
  // Remove todos os caracteres que não são dígitos
  let cleaned = pastedText.replace(/\D/g, '');

  if (!cleaned) {
    return '';
  }

  // Remove código do país Brasil (55) se presente no início
  if (cleaned.startsWith('55') && cleaned.length >= 12) {
    cleaned = cleaned.substring(2);
  }

  // Remove zero inicial (formato antigo de discagem 0XX)
  while (cleaned.startsWith('0') && cleaned.length > 11) {
    cleaned = cleaned.substring(1);
  }

  const len = cleaned.length;

  // Caso 1: Já tem 11 dígitos (DDD + 9 dígitos de celular)
  if (len === 11) {
    const ddd = cleaned.substring(0, 2);
    const numero = cleaned.substring(2);

    // Se o número já começa com 9, está correto
    if (numero[0] === '9') {
      return cleaned;
    }

    // Se não começa com 9 mas tem 9 dígitos, pode ser número sem o 9
    // Exemplo: 48912345678 -> deveria ser 489912345678
    if (numero.length === 9) {
      return ddd + '9' + numero;
    }

    return cleaned;
  }

  // Caso 2: 10 dígitos (DDD + 8 dígitos)
  if (len === 10) {
    const ddd = cleaned.substring(0, 2);
    const numero = cleaned.substring(2);

    // Se já começa com 9, só falta um dígito - erro de formato
    if (numero[0] === '9') {
      // Pode ser um erro, retorna como está
      return cleaned;
    }

    // Se começa com 2, 3 ou 4, é telefone fixo - não adiciona o 9
    if (['2', '3', '4'].includes(numero[0])) {
      return cleaned;
    }

    // Para outros dígitos (5, 6, 7, 8), adiciona o 9 (celular antigo sem o 9)
    return ddd + '9' + numero;
  }

  // Caso 3: 12 dígitos (provável: 0 + DDD + 9 dígitos OU erro de formatação)
  if (len === 12) {
    // Se começa com 0, remove
    if (cleaned[0] === '0') {
      return normalizePhoneNumberPaste(cleaned.substring(1));
    }

    // Tenta pegar os últimos 11 dígitos
    const last11 = cleaned.substring(len - 11);
    if (last11[2] === '9') {
      return last11;
    }

    return cleaned.substring(1);
  }

  // Caso 4: 13 dígitos (55 + DDD + 9 dígitos completo)
  if (len === 13) {
    // Remove os primeiros 2 dígitos (código do país)
    return cleaned.substring(2);
  }

  // Caso 5: 9 dígitos (apenas o número com o 9 inicial - sem DDD)
  if (len === 9) {
    if (cleaned[0] === '9') {
      // Adiciona DDD 48 (padrão Florianópolis)
      return '48' + cleaned;
    }

    // Se não começa com 9, não é um número válido de celular
    // Adiciona DDD 48 e o 9
    return '489' + cleaned;
  }

  // Caso 6: 8 dígitos (apenas o número sem o 9 - sem DDD)
  if (len === 8) {
    // Adiciona DDD 48 e o 9 do celular
    return '489' + cleaned;
  }

  // Caso 7: 7 dígitos ou menos - número incompleto, retorna como está
  if (len < 8) {
    return cleaned;
  }

  // Caso 8: Mais de 13 dígitos - tenta extrair um número válido
  if (len > 13) {
    // Tenta pegar os últimos 11 dígitos
    const last11 = cleaned.substring(len - 11);

    // Verifica se os últimos 11 formam um número válido (terceiro dígito é 9)
    if (last11[2] === '9') {
      return last11;
    }

    // Tenta os últimos 10 e adiciona o 9
    const last10 = cleaned.substring(len - 10);
    if (last10[2] !== '9') {
      const ddd = last10.substring(0, 2);
      const numero = last10.substring(2);
      return ddd + '9' + numero;
    }
  }

  // Fallback: retorna como está ou tenta ajustar
  return cleaned;
}

/**
 * Formata um número de telefone para exibição
 * Ex: 48992033200 -> (48) 99203-3200
 */
export function formatPhoneForDisplay(phoneNumber: string): string {
  // Remove tudo que não é dígito
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Se tem 11 dígitos (DDD + 9 dígitos)
  if (cleaned.length === 11) {
    const ddd = cleaned.substring(0, 2);
    const part1 = cleaned.substring(2, 7);
    const part2 = cleaned.substring(7);
    return `(${ddd}) ${part1}-${part2}`;
  }

  // Se tem 10 dígitos (DDD + 8 dígitos)
  if (cleaned.length === 10) {
    const ddd = cleaned.substring(0, 2);
    const part1 = cleaned.substring(2, 6);
    const part2 = cleaned.substring(6);
    if (['2', '3', '4'].some(prefix => cleaned.startsWith(prefix))){
      return `(${ddd}) ${part1}-${part2}`;
    }
    return `(${ddd}) 9${part1}-${part2}`;
  }

  // Retorna como está se não se encaixar nos formatos conhecidos
  return phoneNumber;
}

/**
 * Valida se um número de telefone está em um formato válido
 */
export function isValidPhoneFormat(phoneNumber: string): boolean {
  const cleaned = phoneNumber.replace(/\D/g, '');
  // Aceita 10 ou 11 dígitos
  return cleaned.length === 10 || cleaned.length === 11;
}
